import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ArrowDown } from 'lucide-react';
import { Logo } from './Logo';

// Hero background video (lightest, ~938 KB — loads instantly on most connections)
const HERO_VIDEO =
  'https://customer-assets.emergentagent.com/job_produtos-gaucha/artifacts/375pfgeb_WhatsApp%20Video%202026-01-24%20at%2021.27.04.mp4';

// Best customer photos — auto-rotating slideshow.
// Picked the most impactful shots from the asset library (rotativa em ação, costelão na brasa, fogo).
const HERO_SLIDES = [
  {
    src: 'https://customer-assets.emergentagent.com/job_produtos-gaucha/artifacts/flk0mvrh_WhatsApp%20Image%202026-01-24%20at%2021.27.04%281%29.jpeg',
    label: 'Costelão raiz',
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_produtos-gaucha/artifacts/jn7ukzsx_WhatsApp%20Image%202026-01-24%20at%2021.27.04%284%29.jpeg',
    label: 'Inox premium',
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_produtos-gaucha/artifacts/9h4phi1r_WhatsApp%20Image%202026-01-24%20at%2021.27.04%282%29.jpeg',
    label: 'Tradicional reforçada',
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_produtos-gaucha/artifacts/l3nkk6gm_WhatsApp%20Image%202026-01-24%20at%2021.27.04%283%29.jpeg',
    label: 'Estrutura robusta',
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_produtos-gaucha/artifacts/m7fudtkz_WhatsApp%20Image%202026-01-24%20at%2021.27.04%285%29.jpeg',
    label: 'Pronta pra brasa',
  },
];

const SLIDE_DURATION_MS = 5500;

const HeroSection = () => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [videoReady, setVideoReady] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const videoRef = useRef(null);

  const openChatbot = () => {
    window.dispatchEvent(new CustomEvent('open-chatbot', { detail: { context: '' } }));
  };

  const scrollToProducts = () => {
    const element = document.getElementById('products');
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  // Auto-advance slideshow (only when video is NOT playing — saves CPU)
  useEffect(() => {
    if (videoReady && !videoFailed) return;
    const id = setInterval(() => {
      setActiveSlide((s) => (s + 1) % HERO_SLIDES.length);
    }, SLIDE_DURATION_MS);
    return () => clearInterval(id);
  }, [videoReady, videoFailed]);

  // Preload all slide images so transitions are smooth (and serve as video fallback)
  useEffect(() => {
    HERO_SLIDES.forEach((s) => {
      const img = new Image();
      img.src = s.src;
    });
  }, []);

  // Try to play video; respect "Save-Data" and slow connections
  useEffect(() => {
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    const saveData = conn?.saveData === true;
    const slowConn = conn && /(2g|slow-2g)/i.test(conn.effectiveType || '');
    if (saveData || slowConn) {
      setVideoFailed(true);
      return;
    }
    const v = videoRef.current;
    if (!v) return;
    const onCanPlay = () => {
      setVideoReady(true);
      v.play().catch(() => setVideoFailed(true));
    };
    const onError = () => setVideoFailed(true);
    v.addEventListener('canplay', onCanPlay);
    v.addEventListener('error', onError);
    return () => {
      v.removeEventListener('canplay', onCanPlay);
      v.removeEventListener('error', onError);
    };
  }, []);

  const goToSlide = useCallback((idx) => {
    setActiveSlide(idx);
  }, []);

  const showVideo = videoReady && !videoFailed;

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Slideshow + video background */}
      <div className="absolute inset-0 bg-stone-950">
        {/* Video layer (only fades in once ready) */}
        <video
          ref={videoRef}
          data-testid="hero-video"
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000"
          style={{ opacity: showVideo ? 0.55 : 0 }}
          src={HERO_VIDEO}
          poster={HERO_SLIDES[0].src}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          aria-hidden="true"
        />
        {/* Slideshow fallback (always rendered; hides when video plays) */}
        <div
          className="absolute inset-0 transition-opacity duration-1000"
          style={{ opacity: showVideo ? 0 : 1 }}
        >
          {HERO_SLIDES.map((slide, idx) => (
            <div
              key={idx}
              className="absolute inset-0 transition-opacity duration-[1400ms] ease-out bg-cover bg-center"
              style={{
                backgroundImage: `url('${slide.src}')`,
                opacity: idx === activeSlide ? 0.55 : 0,
                transform: idx === activeSlide ? 'scale(1.04)' : 'scale(1)',
                transition:
                  'opacity 1400ms ease-out, transform 8000ms ease-out',
              }}
            />
          ))}
        </div>
        {/* Gradient overlays for legibility */}
        <div className="absolute inset-0 bg-gradient-to-b from-stone-950/70 via-stone-900/55 to-stone-950" />
        <div className="absolute inset-0 bg-gradient-to-r from-stone-950/60 via-transparent to-stone-950/40" />
      </div>

      {/* Animated ember particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {Array.from({ length: 20 }).map((_, i) => (
          <span
            key={i}
            className="absolute rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              width: `${2 + Math.random() * 4}px`,
              height: `${2 + Math.random() * 4}px`,
              background: i % 3 === 0 ? '#fbbf24' : '#ea580c',
              opacity: 0.35 + Math.random() * 0.4,
              boxShadow: '0 0 8px rgba(245, 158, 11, 0.6)',
              animation: `emberFloat ${5 + Math.random() * 6}s ease-in-out ${Math.random() * 3}s infinite`,
            }}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-24 pb-12">
        {/* Logo / brand block */}
        <div className="mb-10 flex justify-center animate-fadeInUp">
          <Logo variant="stacked" size="hero" />
        </div>

        {/* Single tagline */}
        <p
          className="text-lg sm:text-2xl md:text-3xl text-stone-100 mb-12 max-w-3xl mx-auto leading-relaxed font-medium animate-fadeInUp"
          style={{ animationDelay: '180ms' }}
        >
          A melhor e a mais{' '}
          <span className="text-amber-500 italic font-bold">bagual</span>{' '}
          churrasqueira rotativa para seu{' '}
          <span className="text-amber-400 font-bold">costelão</span>! 🥩 do Brasil
        </p>

        {/* CTAs */}
        <div
          className="flex flex-col sm:flex-row gap-4 justify-center items-stretch sm:items-center animate-fadeInUp"
          style={{ animationDelay: '320ms' }}
        >
          <button
            data-testid="hero-cta-btn"
            onClick={openChatbot}
            className="group bg-green-600 hover:bg-green-500 text-white px-7 py-4 rounded-xl font-bold text-base sm:text-lg transition-all hover:scale-105 hover:shadow-2xl hover:shadow-green-600/40 flex items-center gap-3 justify-center"
          >
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
            Fazer um Orçamento
          </button>

          <button
            onClick={scrollToProducts}
            className="group border-2 border-amber-500/60 hover:border-amber-500 text-amber-400 hover:text-amber-300 px-7 py-4 rounded-xl font-bold text-base sm:text-lg transition-all hover:bg-amber-500/10 flex items-center gap-3 justify-center"
          >
            Ver Produtos
            <ArrowDown className="w-5 h-5 group-hover:translate-y-1 transition-transform" />
          </button>
        </div>
      </div>

      {/* Slide indicators (only show when video is NOT playing) */}
      {!showVideo && (
        <div className="absolute bottom-20 left-1/2 -translate-x-1/2 flex items-center gap-2 z-10">
          {HERO_SLIDES.map((_, idx) => (
            <button
              key={idx}
              data-testid={`hero-slide-${idx}`}
              onClick={() => goToSlide(idx)}
              aria-label={`Slide ${idx + 1}`}
              className={`h-1.5 rounded-full transition-all ${
                idx === activeSlide
                  ? 'w-10 bg-amber-500'
                  : 'w-2 bg-white/40 hover:bg-white/60'
              }`}
            />
          ))}
        </div>
      )}

      {/* Scroll indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 animate-bounce">
        <ArrowDown className="w-6 h-6 text-amber-500/60" />
      </div>

      <style>{`
        @keyframes emberFloat {
          0%, 100% { transform: translateY(0) translateX(0); opacity: 0.4; }
          50% { transform: translateY(-30px) translateX(10px); opacity: 0.85; }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeInUp {
          animation: fadeInUp 0.8s ease-out both;
        }
      `}</style>
    </section>
  );
};

export default HeroSection;
